import React, { useEffect, useState, useRef } from "react";
import DonationFrequencies from "./Util/DonationFrequencies";
import ProductCustomAmount from "./Util/ProductCustomAmount";
import ProductsDropdown from "./Util/ProductsDropdown";
import AmountButtons from "./Util/AmountButtons";
import CustomInputsPage from "./Util/CustomInputs";
import CustomShimmer from "./Util/Shimmer";
import ProductGroups from "./Util/ProductGroups";
import { setContrast } from "./Util/ColorSwap.js";
import { fieldMapper } from "./Util/FieldMapper";

const freqMap = {
  "": "",
  ONE_TIME: "One-Time",
  DAILY: "Daily",
  WEEKLY: "Weekly",
  MONTHLY: "Monthly",
  ANNUALLY: "Yearly",
};

function ProductForm(props) {
  const [hostedPage, setHostedPage] = useState(null);
  const [elementID, setElementID] = useState("");
  const [configData, setConfigData] = useState({});

  const [maxTransactionAmount, setMaxTransactionAmount] = useState(1000);
  const [customInputsValues, setCustomInputsValues] = useState([]);
  const [primaryColor, setPrimaryColor] = useState("#69995E");
  const [secondaryColor, setSecondaryColor] = useState("#496b42");
  const [funds, setFunds] = useState([
    "General Fund",
    "Zakat Fund",
    "Sadaqah Fund",
  ]);
  const [frequencies, setFrequencies] = useState([
    "ONE_TIME",
    "DAILY",
    "WEEKLY",
    "MONTHLY",
    "ANNUALLY",
  ]);
  const [oneTimeAmounts, setOneTimeAmounts] = useState([
    "10",
    "25",
    "50",
    "100",
  ]);
  const [dailyAmounts, setDailyAmounts] = useState(["10", "25", "50", "100"]);
  const [weeklyAmounts, setWeeklyAmounts] = useState(["10", "25", "50", "100"]);
  const [monthlyAmounts, setMonthlyAmounts] = useState([
    "10",
    "25",
    "50",
    "100",
  ]);
  const [annuallyAmounts, setAnnuallyAmounts] = useState([
    "10",
    "25",
    "50",
    "100",
  ]);
  const [defaultOneTimeAmount, setDefaultOneTimeAmount] = useState("10");
  const [defaultDailyAmount, setDefaultDailyAmount] = useState("25");
  const [defaultWeeklyAmount, setDefaultWeeklyAmount] = useState("50");
  const [defaultMonthlyAmount, setDefaultMonthlyAmount] = useState("100");
  const [defaultAnnuallyAmount, setDefaultAnnuallyAmount] = useState("10");
  const [productGroups, setProductGroups] = useState([]);
  const [selectedProductGroup, setSelectedProductGroup] = useState(undefined);
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1);
  const [quantity, setQuantity] = useState(1);

  const [page, setPage] = useState("START_PAGE");
  const [defaultAmounts, setDefaultAmounts] = useState(oneTimeAmounts);

  const [frequency, setFrequency] = useState("");
  const [selectedFund, setSelectedFund] = useState("");
  //   const [selectedProduct, setSelectedProduct] = useState(undefined);

  const [errorMessage, setErrorMessage] = useState("");
  const [errorDisplayTime, setErrorDisplayTime] = useState(0);
  const [errorMessageInterval, setErrorMessageInterval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addToCart, setAddToCart] = useState(false);

  const productGroupsRef = useRef([]);

  const [domID, setDomID] = useState("");

  const listener = (event) => {
    if (
      !(
        event.data.entity == "CHARITYSTACK" &&
        event.data.action == "RESET_ELEMENT"
      )
    )
      return;

    setPage("START_PAGE");
    setSelectedProductIndex(-1);
    setSelectedProductGroup(undefined); // Have to use a ref here because it can't properly read the state for some reason
    // setSelectedFund("");

    props.fetchElementsConfig().catch((err) => console.log("ERROR", err));
  };

  const goBackListener = (event) => {
    if (
      !(event.data.entity == "CHARITYSTACK" && event.data.action == "GO_BACK")
    )
      return;

    // this looks complicated b/c we can't read the states inside this function the normal way
    // tbh right now we can always set the state as START_PAGE b/c both if and else do that
    // but i am keeping this in case we need to actually change it to two or more values later
    setPage((currentPage) => {
      if (currentPage === "START_PAGE") {
        return "START_PAGE";
      }
      return "START_PAGE";
    });
  };

  useEffect(() => {
    // Send the content height to the parent window
    window.parent.postMessage(
      {
        action: "CHANGE_PAGE",
        entity: "CHARITYSTACK",
        sender: "CROWDFUNDING_FORM",
        receiver: "HOSTED_PAGE",
        data: JSON.stringify({
          currentPage: page,
        }),
      },
      "*",
    );
    resizeIframe();
  }, [page]);

  useEffect(() => {
    if (loading === false && hostedPage !== null) {
      // send message that element has finished loading
      window.parent.postMessage(
        {
          action: "FINISHED_LOADING",
          entity: "CHARITYSTACK",
          sender: "CROWDFUNDING_FORM",
          receiver: "HOSTED_PAGE",
        },
        "*",
      );
    }
  }, [loading]);

  useEffect(() => {
    resizeIframe();
  }, [loading, page, selectedProductGroup]);

  useEffect(() => {
    window.addEventListener("message", listener);
    window.addEventListener("message", goBackListener);
    // grab hostedPage boolean
    const urlParams = new URLSearchParams(window.location.search);
    const hostedPage = urlParams.get("page");
    // page can be either null, HOSTED_PAGE_FULLSCREEN, or HOSTED_PAGE_POPUP
    setHostedPage(hostedPage);
    setDomID(urlParams.get("domID"));
    return () => {
      window.removeEventListener("message", listener);
      window.removeEventListener("message", goBackListener);
    };
  }, []);

  useEffect(() => {
    const { data } = props;
    // preprocess data
    data.customInputs.forEach((input) => {
      input.answers = [""];
    });
    // convert amounts to strings
    data.oneTimeAmounts = data.oneTimeAmounts.map((amount) =>
      amount.toString(),
    );
    data.dailyAmounts = data.dailyAmounts.map((amount) => amount.toString());
    data.weeklyAmounts = data.weeklyAmounts.map((amount) => amount.toString());
    data.monthlyAmounts = data.monthlyAmounts.map((amount) =>
      amount.toString(),
    );
    data.annuallyAmounts = data.annuallyAmounts.map((amount) =>
      amount.toString(),
    );
    data.defaultOneTimeAmount = data.defaultOneTimeAmount.toString();
    data.defaultDailyAmount = data.defaultDailyAmount.toString();
    data.defaultWeeklyAmount = data.defaultWeeklyAmount.toString();
    data.defaultMonthlyAmount = data.defaultMonthlyAmount.toString();
    data.defaultAnnuallyAmount = data.defaultAnnuallyAmount.toString();

    setElementID(data.elementID);
    setProductGroups(data.productGroups);
    setSelectedProductGroup(data.productGroups[0]);
    // setSelectedProductGroup(undefined);
    setMaxTransactionAmount(data.maxTransactionAmount);
    setCustomInputsValues(data.customInputs);
    setFunds(data.funds);
    if (data.funds.length == 1) {
      setSelectedFund(data.funds[0]);
    }
    setFrequencies(data.frequencies);
    setFrequency(data.defaultFrequency);
    setOneTimeAmounts(data.oneTimeAmounts);
    setDailyAmounts(data.dailyAmounts);
    setWeeklyAmounts(data.weeklyAmounts);
    setMonthlyAmounts(data.monthlyAmounts);
    setAnnuallyAmounts(data.annuallyAmounts);
    setDefaultOneTimeAmount(data.defaultOneTimeAmount);
    setDefaultDailyAmount(data.defaultDailyAmount);
    setDefaultWeeklyAmount(data.defaultWeeklyAmount);
    setDefaultMonthlyAmount(data.defaultMonthlyAmount);
    setDefaultAnnuallyAmount(data.defaultAnnuallyAmount);
    setPrimaryColor(data.lightColor);
    setSecondaryColor(data.darkColor);
    setConfigData(data);
    setAddToCart(data.showAddToCart === undefined ? null : data.showAddToCart);
    productGroupsRef.current = data.productGroups;
    setLoading(false);
  }, [props.data]);

  useEffect(() => {
    let timer;
    if (errorMessage != "") {
      timer = setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [errorMessage]);

  useEffect(() => {
    if (selectedProductGroup && selectedProductGroup.products.length == 1) {
      setSelectedProductIndex(0);
    } else {
      setSelectedProductIndex(-1);
    }
    console.log("productsGroup", selectedProductGroup);
  }, [selectedProductGroup]);

  //   useEffect(() => {
  //     if (frequency == "ONE_TIME") {
  //       // grab the crowdfunding option that matches the amount
  //       setSelectedCrowdfundingOption(
  //         defaultOneTimeAmount == "0"
  //           ? undefined
  //           : crowdfundingOptions.find(
  //               (option) => option.amount == defaultOneTimeAmount,
  //             ) ?? {
  //               amount: defaultOneTimeAmount,
  //               title: "",
  //               description: "",
  //             },
  //       );
  //     }
  //     if (frequency == "DAILY") {
  //       setSelectedCrowdfundingOption(
  //         defaultDailyAmount == "0"
  //           ? undefined
  //           : crowdfundingOptions.find(
  //               (option) => option.amount == defaultDailyAmount,
  //             ) ?? {
  //               amount: defaultDailyAmount,
  //               title: "",
  //               description: "",
  //             },
  //       );
  //     }
  //     if (frequency == "WEEKLY") {
  //       setSelectedCrowdfundingOption(
  //         defaultWeeklyAmount == "0"
  //           ? undefined
  //           : crowdfundingOptions.find(
  //               (option) => option.amount == defaultWeeklyAmount,
  //             ) ?? {
  //               amount: defaultWeeklyAmount,
  //               title: "",
  //               description: "",
  //             },
  //       );
  //     }
  //     if (frequency == "MONTHLY") {
  //       setSelectedCrowdfundingOption(
  //         defaultMonthlyAmount == "0"
  //           ? undefined
  //           : crowdfundingOptions.find(
  //               (option) => option.amount == defaultMonthlyAmount,
  //             ) ?? {
  //               amount: defaultMonthlyAmount,
  //               title: "",
  //               description: "",
  //             },
  //       );
  //     }
  //     if (frequency == "ANNUALLY") {
  //       setSelectedCrowdfundingOption(
  //         defaultAnnuallyAmount == "0"
  //           ? undefined
  //           : crowdfundingOptions.find(
  //               (option) => option.amount == defaultAnnuallyAmount,
  //             ) ?? {
  //               amount: defaultAnnuallyAmount,
  //               title: "",
  //               description: "",
  //             },
  //       );
  //     }
  //   }, [frequency]);

  useEffect(() => {
    if (errorDisplayTime >= 3) {
      clearInterval(errorMessageInterval);
      setErrorDisplayTime(0);
      setErrorMessage("");
    }
  }, [errorDisplayTime]);

  const resizeIframe = () => {
    const height = document.body.scrollHeight;
    const width = document.body.scrollWidth;
    console.log("resizing", elementID, height, width);

    // Send the content height to the parent window
    window.parent.postMessage(
      {
        action: "RESIZE",
        entity: "CHARITYSTACK",
        sender: "EMBED_FORM",
        receiver: "HOSTED_SCRIPT",
        data: JSON.stringify({
          height: height,
          width: width,
          elementID: elementID,
          domID: domID,
        }),
      },
      "*",
    );
  };

  const hasErrors = () => {
    if (frequency == "") {
      setErrorMessage("Please select a frequency");
      return true;
    }
    if (selectedProductIndex == -1) {
      setErrorMessage("Please select an option");
      return true;
    }
    // if (+selectedProductGroup.products[selectedProductIndex].amount < 1) {
    //   setErrorMessage("Please select an amount that is at least $1");
    //   return true;
    // }
    // if (selectedFund == "") {
    //   setErrorMessage("Please select a fund");
    //   return true;
    // }
    return false;
  };

  const handleButtonClick = () => {
    if (hasErrors()) {
      return;
    }
    if (page === "START_PAGE" && customInputsValues.length > 0) {
      setPage("CUSTOM_INPUTS_PAGE");
    } else {
      handleDonate();
      setQuantity(1);
    }
  };

  const handlePlusClick = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
    triggerButtonAnimation("plus-button");
  };

  const handleMinusClick = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
      triggerButtonAnimation("minus-button");
    }
  };

  const triggerButtonAnimation = (buttonId) => {
    const button = document.getElementById(buttonId);
    button.classList.add("scale-110");
    setTimeout(() => {
      button.classList.remove("scale-110");
    }, 100);
  };

  const handleDonate = () => {
    const data = configData;

    // updated configData to include user inputs
    data.frequency = frequency;
    data.products = [
      {
        productGroupName: selectedProductGroup.productGroupName,
        productGroupDescription: selectedProductGroup.productGroupDescription,
        productID:
          selectedProductGroup.products[selectedProductIndex].productID,
        productName:
          selectedProductGroup.products[selectedProductIndex].productName,
        productPrice:
          selectedProductGroup.products[selectedProductIndex].productPrice,
        quantity: quantity,
      },
    ];
    // data.fund = selectedProductGroup.products[selectedProductIndex].productName;
    data.fund = selectedFund;
    data.customInputsValues = customInputsValues ?? [];
    data.amount =
      selectedProductGroup.products[selectedProductIndex].productPrice;

    // convert amounts to float. Can remove later since not needed on donation form side
    data.oneTimeAmounts = data.oneTimeAmounts.map((amount) =>
      parseFloat(amount),
    );
    data.dailyAmounts = data.dailyAmounts.map((amount) => parseFloat(amount));
    data.weeklyAmounts = data.weeklyAmounts.map((amount) => parseFloat(amount));
    data.monthlyAmounts = data.monthlyAmounts.map((amount) =>
      parseFloat(amount),
    );
    data.annuallyAmounts = data.annuallyAmounts.map((amount) =>
      parseFloat(amount),
    );
    data.defaultOneTimeAmount = parseFloat(data.defaultOneTimeAmount);
    data.defaultDailyAmount = parseFloat(data.defaultDailyAmount);
    data.defaultWeeklyAmount = parseFloat(data.defaultWeeklyAmount);
    data.defaultMonthlyAmount = parseFloat(data.defaultMonthlyAmount);
    data.defaultAnnuallyAmount = parseFloat(data.defaultAnnuallyAmount);

    data.hostedPage = hostedPage;

    data.mappedData = fieldMapper(
      data.fieldMappings,
      data.fund,
      data.customInputsValues,
      data.products,
    );

    if (addToCart === true) {
      window.top.postMessage(
        {
          action: "UPDATE_CART",
          entity: "CHARITYSTACK",
          sender: "CROWDFUNDING_FORM",
          receiver: "HOSTED_SCRIPT",
          data: JSON.stringify(data),
          id: data.cartElementID,
          quantity: quantity,
        },
        "*",
      );
    } else {
      // create pseudo cartItems array since products can be multiple
      data.cartItems = [];
      // Initialize with empty default amounts array
      data.defaultAmounts = [];
      // create cartItemKey
      let cartItemKey = `${data.frequency}_${data.fund}_${data.amount}_${data.formName}_${data.products[0].productName}_${data.products[0].productGroupName}_${data.products[0].productPrice}`;
      const cartItem = {
        cartItemKey: cartItemKey,
        salesforceCampaignID: data.salesforceCampaignID,
        frequency: data.frequency,
        fundName: data.fund,
        amount: parseFloat(data.amount),
        quantity: quantity,
        fundraiser: data.formName,
        formID: data.formID,
        crowdfundingOption: data.crowdfundingOption,
      };
      if (data.products) {
        cartItem.productID = data.products[0].productID;
        cartItem.productGroupName = data.products[0].productGroupName;
        cartItem.productGroupDescription =
          data.products[0].productGroupDescription;
        cartItem.productName = data.products[0].productName;
        cartItem.productPrice = data.products[0].productPrice;
        cartItem.quantity = data.products[0].quantity;
      }
      // add each key value pair in mappedData to cartItem
      if (data.mappedData) {
        Object.entries(data.mappedData).forEach(([key, value]) => {
          cartItem[key] = value;
        });
      }

      data.customInputsValues.forEach((customInput) => {
        customInput["cartItemKey"] = cartItemKey;
      });

      data.cartItems = [cartItem];

      // send message to checkout
      window.top.postMessage(
        {
          action: "DONATE",
          entity: "CHARITYSTACK",
          sender: "CROWDFUNDING_FORM",
          receiver: "HOSTED_SCRIPT",
          hostedPage: hostedPage,
          donationData: JSON.stringify(data),
          quantity: quantity,
        },
        "*",
      );
    }

    // }, window.location.ancestorOrigins[0]);
  };
  if (loading) {
    return (
      <>
        <div className="container mx-auto h-full max-w-sm items-center">
          <div className="rounded-md rounded-b-none border-x border-t border-gray-200 bg-white py-2 text-center text-gray-500">
            Loading
          </div>
          <div className="grid grid-cols-1 rounded-md rounded-t-none border border-gray-200 bg-white px-6 pb-4 pt-2 sm:px-6">
            <CustomShimmer />
          </div>
        </div>
      </>
    );
  } else {
    if (configData.active === false) {
      return (
        <>
          <div className="container mx-auto h-full max-w-sm items-center">
            <div className="rounded-md rounded-b-none border-x border-t border-gray-200 bg-white py-2 text-center text-gray-500">
              Deactivated Form: {configData.formName}
            </div>
            <div className="grid h-64 grid-cols-1 rounded-md rounded-t-none border border-gray-200 bg-white px-6 pb-4 pt-2 sm:px-6"></div>
          </div>
        </>
      );
    } else {
      return (
        <div data-name={configData.elementName}>
          {page === "START_PAGE" && (
            <div
              className={`container mx-auto h-full items-center ${
                hostedPage !== null ? "max-w-lg" : "max-w-sm"
              }`}
            >
              {hostedPage === null && (
                <div
                  className={`rounded-md rounded-b-none border-x border-t border-gray-200 bg-white py-2 text-center text-gray-700`}
                >
                  {configData.formName}
                  {/* {frequencies.length > 1
                    ? "Donation Information"
                    : `${freqMap[frequency]} Donation`} */}
                </div>
              )}
              <div
                className={`grid grid-cols-1 bg-white ${
                  hostedPage !== null
                    ? ""
                    : "rounded-md rounded-t-none border border-gray-200 px-6 pb-4 pt-2 sm:px-6"
                }`}
              >
                <div className="w-full">
                  <DonationFrequencies
                    frequencies={frequencies}
                    frequency={frequency}
                    setFrequency={setFrequency}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                  />
                </div>
                <div className="pb-2">
                  <ProductCustomAmount
                    amount={
                      selectedProductGroup &&
                      selectedProductIndex !== -1 &&
                      selectedProductIndex <
                        selectedProductGroup.products.length
                        ? selectedProductGroup.products[selectedProductIndex]
                            .productPrice * quantity
                        : ""
                    }
                    quantity={quantity}
                    basePrice={
                      selectedProductGroup &&
                      selectedProductIndex !== -1 &&
                      selectedProductIndex <
                        selectedProductGroup.products.length
                        ? selectedProductGroup.products[selectedProductIndex]
                            .productPrice
                        : ""
                    }
                    setAmount={() => {}}
                    setQuantity={() => {}}
                    setBasePrice={() => {}}
                    maxTransactionAmount={maxTransactionAmount}
                    disabled={true}
                  />
                </div>
                <div className="pb-2">
                  <ProductGroups
                    primaryColor={primaryColor}
                    selectedProductGroup={selectedProductGroup}
                    setSelectedProductGroup={setSelectedProductGroup}
                    productGroups={productGroups}
                    resizeIframe={resizeIframe}
                  />

                  {/* <CrowdfundingOptions
                    primaryColor={primaryColor}
                    selectedCrowdfundingOption={selectedCrowdfundingOption}
                    setSelectedCrowdfundingOption={
                      setSelectedCrowdfundingOption
                    }
                    crowdfundingOptions={crowdfundingOptions}
                    resizeIframe={resizeIframe}
                  /> */}
                </div>
                {/* {funds.length > 1 && (
                  <div className="pb-2">
                    <FundsDropdown
                      funds={funds}
                      selectedFund={selectedFund}
                      setSelectedFund={setSelectedFund}
                    />
                  </div>
                )} */}
                <div
                  className={`pb-2 ${
                    selectedProductGroup &&
                    selectedProductGroup.products.length > 1
                      ? ""
                      : "hidden"
                  }`}
                >
                  <ProductsDropdown
                    products={
                      selectedProductGroup ? selectedProductGroup.products : []
                    }
                    selectedProductIndex={selectedProductIndex}
                    setSelectedProductIndex={setSelectedProductIndex}
                    productGroup={selectedProductGroup}
                  />
                </div>

                <div className={`pb-2 ${selectedProductGroup ? "" : "hidden"}`}>
                  <div className="mt-1 flex items-center justify-between rounded-md border border-gray-300 p-2">
                    <span className="text-sm text-gray-500">Quantity</span>
                    <div className="flex items-center">
                      <button
                        id="minus-button"
                        className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-600 focus:outline-none"
                        onClick={handleMinusClick}
                        style={{ fontSize: "10px" }} // Adjust the font size as needed
                      >
                        &minus;
                      </button>
                      <input
                        type="text"
                        className="w-7 border-none bg-white text-center focus:outline-none"
                        value={quantity}
                        readOnly
                      />
                      <button
                        id="plus-button"
                        className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-600 focus:outline-none"
                        onClick={handlePlusClick}
                        style={{ fontSize: "12px" }} // Adjust the font size as needed
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={
                    "mt-4 h-10 rounded-md px-3.5 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2" +
                    (errorMessage !== "" ? " text-sm" : " text-lg")
                  }
                  onClick={handleButtonClick}
                  style={{
                    backgroundColor: primaryColor,
                    color: setContrast(primaryColor),
                  }}
                >
                  {errorMessage !== ""
                    ? errorMessage
                    : customInputsValues.length == 0
                      ? addToCart === true
                        ? "Add to Cart"
                        : "Donate"
                      : "Next"}
                </button>
              </div>
            </div>
          )}
          {page === "CUSTOM_INPUTS_PAGE" && (
            <CustomInputsPage
              customInputsValues={customInputsValues}
              setCustomInputsValues={setCustomInputsValues}
              setPage={setPage}
              handleDonate={handleDonate}
              primaryColor={primaryColor}
              hostedPage={hostedPage}
              addToCart={addToCart}
            />
          )}
        </div>
      );
    }
  }
}

export default ProductForm;
